import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { authHeaders, http } from './http.js';
import { assertExtensionInstalled, openMessageInSunat } from '../utils/sunatExtension.js';

async function listMessages(getToken, companyId, page, size) {
    const headers = await authHeaders(getToken);
    const url = companyId ? `/messages/company/${encodeURIComponent(companyId)}/recent` : '/messages/recent';
    const { data } = await http.get(url, {
        headers,
        params: { page, size },
    });
    return data;
}

// Without a companyId, lists the messages of all companies. Both are newest first.
export function useMessages(companyId, page, size) {
    const { getToken } = useAuth();
    return useQuery({
        queryKey: ['messages', companyId ?? 'all', page, size],
        queryFn: () => listMessages(getToken, companyId, page, size),
        placeholderData: (previousData) => previousData,
    });
}

async function createSunatLink(getToken, messageId) {
    const headers = await authHeaders(getToken);
    const { data } = await http.post(`/messages/${encodeURIComponent(messageId)}/sunat-link`, null, { headers });
    return data;
}

// Opens the message in SUNAT's Buzón Electrónico through the browser extension. The backend hands out a
// single-use token, and the extension exchanges it for the SOL credentials, so they never reach this page.
export function useOpenInSunat() {
    const { getToken } = useAuth();
    return useMutation({
        mutationFn: async (messageId) => {
            // Checked first so a missing extension doesn't burn a token.
            await assertExtensionInstalled();
            const { token } = await createSunatLink(getToken, messageId);
            await openMessageInSunat(token);
        },
    });
}

async function triggerMessages(getToken) {
    const headers = await authHeaders(getToken);
    await http.post('/messages', null, { headers });
}

// Queues the retrieve-messages process for every company. The backend only queues the work and
// returns, so new messages show up later; a per-company failure is logged server-side, not returned.
export function useTriggerMessages() {
    const { getToken } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => triggerMessages(getToken),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['messages'] });
        },
    });
}
